import { useRef, useState } from 'react'
import { ImagePlus, Paperclip, X } from 'lucide-react'
import { Button } from '@/components/ui/Button'

const ACCEPT = 'image/png,image/jpeg,image/webp,application/pdf'
const MAX_FILES = 5
const MAX_MB = 5

interface BlocoAttachmentFieldProps {
  files: File[]
  onChange: (files: File[]) => void
  disabled?: boolean
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  return `${(bytes / 1024).toFixed(0)} KB`
}

export function BlocoAttachmentField({ files, onChange, disabled = false }: BlocoAttachmentFieldProps) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [pasteHint, setPasteHint] = useState(false)

  const addFiles = (incoming: File[]) => {
    const valid = incoming.filter((file) => {
      if (file.size > MAX_MB * 1024 * 1024) return false
      return file.type.startsWith('image/') || file.type === 'application/pdf'
    })
    if (!valid.length) return
    const merged = [...files, ...valid].slice(0, MAX_FILES)
    onChange(merged)
  }

  const handlePaste = (event: React.ClipboardEvent) => {
    const items = event.clipboardData?.items
    if (!items) return
    const pasted: File[] = []
    for (const item of items) {
      if (item.type.startsWith('image/')) {
        const file = item.getAsFile()
        if (file) pasted.push(file)
      }
    }
    if (pasted.length) {
      event.preventDefault()
      addFiles(pasted)
      setPasteHint(true)
      window.setTimeout(() => setPasteHint(false), 2000)
    }
  }

  const removeFile = (index: number) => {
    onChange(files.filter((_, i) => i !== index))
  }

  return (
    <div
      className='mt-3 rounded-lg border border-dashed border-[var(--glass-border)] bg-bg-card/50 p-3'
      onPaste={handlePaste}
    >
      <div className='mb-2 flex items-center justify-between gap-2'>
        <span className='text-xs font-medium text-text-muted'>Print / anexos</span>
        <div className='flex gap-1'>
          <Button
            type='button'
            size='sm'
            variant='secondary'
            disabled={disabled || files.length >= MAX_FILES}
            icon={<Paperclip size={14} />}
            onClick={() => inputRef.current?.click()}
          >
            Arquivo
          </Button>
          <input
            ref={inputRef}
            type='file'
            accept={ACCEPT}
            multiple
            className='hidden'
            onChange={(e) => {
              addFiles(Array.from(e.target.files ?? []))
              e.target.value = ''
            }}
          />
        </div>
      </div>

      <p className='text-[11px] text-text-muted'>
        Cole um print (Ctrl+V) ou envie imagem/PDF — máx. {MAX_MB} MB, até {MAX_FILES} arquivos.
      </p>
      {pasteHint && (
        <p className='mt-1 text-[11px] font-medium text-[var(--brand-primary)]'>Print colado!</p>
      )}

      {files.length > 0 && (
        <ul className='mt-2 space-y-1'>
          {files.map((file, index) => (
            <li
              key={`${file.name}-${index}`}
              className='flex items-center gap-2 rounded-md bg-[var(--glass-bg)] px-2 py-1 text-xs text-text-primary'
            >
              <ImagePlus size={12} className='shrink-0 text-text-muted' />
              <span className='min-w-0 flex-1 truncate'>{file.name || 'print.png'}</span>
              <span className='text-text-muted'>{formatSize(file.size)}</span>
              <button
                type='button'
                onClick={() => removeFile(index)}
                className='text-text-muted hover:text-red-500'
                aria-label='Remover anexo'
              >
                <X size={14} />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
